import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { BookingService } from '../services/bookingService';

const BOOKING_BASE_HOST = import.meta.env.VITE_BOOKING_BASE_HOST as string | undefined;

export interface TenantContextValue {
  /** tenantId resuelto por subdominio (host) o null si no aplica */
  tenantId: string | null;
  /** nombre del tenant cuando se resolvió por host */
  tenantName: string | null;
  /** true cuando el tenant viene del host (subdominio), no de la URL */
  isFromSubdomain: boolean;
  /** carga inicial de resolución por host */
  loading: boolean;
  /** error al resolver por host (ej. tenant no encontrado) */
  error: string | null;
}

const TenantContext = createContext<TenantContextValue | null>(null);

function getHost(): string {
  if (typeof window === 'undefined') return '';
  return window.location.host || '';
}

/**
 * Comprueba si el host actual coincide con patrón de subdominio de booking
 * (ej. negocio1.booking.tudominio.com cuando BOOKING_BASE_HOST = booking.tudominio.com).
 */
function hostMatchesSubdomainPattern(host: string): boolean {
  if (!BOOKING_BASE_HOST || !host) return false;
  const base = BOOKING_BASE_HOST.split(':')[0].toLowerCase().trim();
  const hostname = host.split(':')[0].toLowerCase().trim();
  if (!hostname.endsWith(base) || hostname.length <= base.length + 1) return false;
  if (hostname[hostname.length - base.length - 1] !== '.') return false;
  const subdomain = hostname.slice(0, hostname.length - base.length - 1);
  return subdomain.length > 0;
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const host = getHost();
    if (!hostMatchesSubdomainPattern(host)) {
      setLoading(false);
      setTenantId(null);
      setTenantName(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    BookingService.resolveTenantByHost(host)
      .then((tenant) => {
        if (cancelled) return;
        if (tenant) {
          setTenantId(tenant.tenantId);
          setTenantName(tenant.name);
          setError(null);
        } else {
          setTenantId(null);
          setTenantName(null);
          setError('Tenant not found for this subdomain');
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setTenantId(null);
          setTenantName(null);
          setError(err?.message || 'Failed to resolve tenant');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenantId,
      tenantName,
      isFromSubdomain: !!tenantId && !!tenantName,
      loading,
      error,
    }),
    [tenantId, tenantName, loading, error],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    return {
      tenantId: null,
      tenantName: null,
      isFromSubdomain: false,
      loading: false,
      error: null,
    };
  }
  return ctx;
}
